'use client'

import { useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import gsap from 'gsap'

import Icon from '@/components/Icon/Icon'
import OrderDropdown from '@/components/OrderDropdown/OrderDropdown'
import RestaurantOpenClosedBlurbNewFormat from '@/components/RestaurantOpenClosedBlurbNewFormat/RestaurantOpenClosedBlurbNewFormat'

import useBreakpoint from '@/hooks/use-breakpoint'
import useI18n from '@/hooks/use-i18n'

import useStore from '@/store'

import { LocationHero } from '@/types/sections/LocationHero'

import styles from './RestaurantNavigation.module.scss'

type RestaurantNavigationProps = {
  className?: string
}

const CHECKBOX_ID = 'preferredCheckbox'
const DURATION = 0.8

// Tagged on the two clickable surfaces only (label + visible circle), never on the
// wrapping container: this content renders twice (mobile + desktop) sharing CHECKBOX_ID,
// so a label click also dispatches a synthetic click on the other copy's input. Tagging
// the container would make both of those match GTM's [data-gtm-event-name] * trigger and
// send the event twice per click.
const SET_AS_PREFERRED_EVENT = 'location_setAsPreferredCTA'

// Read by GTM into the event parameters `preferred_state` and `preferred_location`.
// The GTM variables walk up from the clicked element, so these must sit on the same two
// clickable surfaces as SET_AS_PREFERRED_EVENT — a click can land on a child (the inner
// circle span or the check icon).
const PREFERRED_STATE_ON = 'on'
const PREFERRED_STATE_OFF = 'off'

const RestaurantNavigation = ({ className }: RestaurantNavigationProps) => {
  const activeRestaurant = useStore(state => state.activeRestaurant)
  const setFavouriteRestaurant = useStore(state => state.setFavouriteRestaurant)
  const favouriteRestaurant = useStore(state => state.favouriteRestaurant)
  const [data, setData] = useState<LocationHero | null>(null)
  const { i18n } = useI18n()
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigationRef = useRef<HTMLElement | null>(null)
  const { isMobile } = useBreakpoint()

  useEffect(() => {
    let show = Boolean(data)
    if (!activeRestaurant) {
      show = false
    }

    gsap.killTweensOf([navigationRef.current])
    gsap.to(navigationRef.current, {
      duration: DURATION,
      y: show ? 0 : '105%',
      ease: 'Power3.easeOut',
    })
  }, [data, activeRestaurant])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setTimeout(() => {
      setData(activeRestaurant)
    }, DURATION * 1.1 * 1000)
  }, [activeRestaurant])

  const isPreferred = favouriteRestaurant?.slug === data?.slug

  // GTM reads these at click time, so this render still reflects the pre-click state:
  // clicking while already preferred turns the preference off, and vice versa.
  const preferredEventAttributes = {
    'data-gtm-event-name': SET_AS_PREFERRED_EVENT,
    'data-gtm-preferred-state': isPreferred ? PREFERRED_STATE_OFF : PREFERRED_STATE_ON,
    'data-gtm-preferred-location': data?.slug,
  }

  const setAsPreferredContent = process.env.NEXT_PUBLIC_IS_CANADA_WEBSITE !== 'true' ? (
    <div className={styles.setAsPreferredContainer}>
      <input
        type="checkbox"
        id={CHECKBOX_ID}
        name={CHECKBOX_ID}
        className={styles.checkboxInput}
        checked={isPreferred}
        onChange={e => {
          if (e.target.checked) {
            setFavouriteRestaurant(data)
          } else {
            setFavouriteRestaurant(null)
          }
        }}
      />
      <label
        className={styles.setAsPreferred__label}
        htmlFor={CHECKBOX_ID}
        {...preferredEventAttributes}
      >
        {i18n('setAsPreferred')}
      </label>
      <span
        className={styles.checkboxBox}
        {...preferredEventAttributes}
        onClick={() => {
          // Compare against the restaurant on screen, like the checkbox above: branching
          // on `favouriteRestaurant` alone cleared a *different* preferred location
          // instead of selecting this one, which also made `preferred_state` wrong.
          if (isPreferred) {
            setFavouriteRestaurant(null)
          } else {
            setFavouriteRestaurant(data)
          }
        }}
      >
        <span className={styles.checkboxBox__circle} />
        <Icon
          name="check"
          className={styles.checkboxBox__check}
        />
      </span>
    </div>
  ) : null

  if (!data) return null

  return (
    <nav
      className={classnames(styles.RestaurantNavigation, className)}
      ref={navigationRef}
    >
      <div className={styles.inner}>
        <div className={styles.leftSide}>
          <div className={styles.blurbContainer}>
            <RestaurantOpenClosedBlurbNewFormat
              timeZone={data.timeZone}
              weeklyOpeningHours={data.weeklyOpeningHours}
              holidayHours={data.holidayHours}
              className={styles.restaurantOpenClosedBlurb}
              isComingSoon={data.isComingSoon}
              isSoftOpening={data.isSoftOpening}
              isTemporarilyClosed={data.isTemporarilyClosed}
            />
            <div className={styles.setAsPreferredMobile}>{setAsPreferredContent}</div>
          </div>
          <p className={styles.title}>
            {data.title}
            {data?.areaTitle && <span>&nbsp;-&nbsp;{data?.areaTitle}</span>}
          </p>
        </div>
        <div className={styles.rightSide}>
          <div className={styles.setAsPreferredDesktop}>{setAsPreferredContent}</div>
          {data?.reservationLinks && (
            <OrderDropdown
              serviceLinks={data?.reservationLinks}
              className={styles.reserveButton}
              primary
              isReserve
              dropdownPosition={isMobile ? 'topLeft' : 'topRight'}
            />
          )}
          {data?.serviceLinks && !data?.isSoftOpening && (
            <OrderDropdown
              serviceLinks={data?.serviceLinks}
              dropdownPosition="topRight"
              label={i18n('order')}
              className={styles.orderDropdown}
            />
          )}
        </div>
      </div>
    </nav>
  )
}

RestaurantNavigation.displayName = 'RestaurantNavigation'

export default RestaurantNavigation
